import * as React from "react";
import {
  Box,
  Typography,
  Stack,
  Paper,
  Button,
  CircularProgress,
  useTheme,
  useMediaQuery,
  IconButton,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";
import Checkbox from "@mui/material/Checkbox";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import TextField from "@mui/material/TextField";
import { useI18n } from "../../i18n/I18nProvider";
import { supabase } from "../../logic/client";


const priorityOptions = [
  { key: "mental", en: "Mental strength & staying calm", de: "Mentale Stärke & Ruhe bewahren" },
  { key: "plan", en: "A clear action plan", de: "Ein klarer Aktionsplan" },
  { key: "info", en: "Information & orientation", de: "Informationen & Orientierung" },
  { key: "family", en: "Safety for my family", de: "Sicherheit für meine Familie" },
  { key: "connection", en: "Connection to others", de: "Verbindung zu anderen Menschen" },
];

type WorkshopStatus = "loading" | "ready" | "no-workshop" | "not-participant" | "error";

type Props = {
  handlePageChange: (page: string) => void;
  role: string;
};

export default function WorkshopDetails(props: Props) {
  const { handlePageChange, role } = props;
  const { lang } = useI18n();
  const L = (en: string, de: string) => (lang === "de" ? de : en);
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const [workshop, setWorkshop] = React.useState<any>(null);
  const [workshopStatus, setWorkshopStatus] = React.useState<WorkshopStatus>("loading");
  const [participants, setParticipants] = React.useState<number>(0);
  const [modules, setModules] = React.useState<any[]>([]);

  const [employeeId, setEmployeeId] = React.useState<string | null>(null);

  const [priorities, setPriorities] = React.useState<string[]>([]);
  const [logistics, setLogistics] = React.useState({
    transport: "",
    drink: "",
    food: "",
  });

  React.useEffect(() => {
    let mounted = true;

    async function init() {
      setWorkshopStatus("loading");

      try {
        // 1. employee first
        const { data: emp } = await supabase.rpc("my_employee_id");
        if (!emp) {
          if (mounted) setWorkshopStatus("not-participant");
          return;
        }

        if (mounted) setEmployeeId(emp);

        // 2. workshop
        const { data: ws, error: workshopError } = await supabase
          .from("workshops")
          .select("*")
          .eq("is_active", true)
          .order("workshop_date", { ascending: true })
          .limit(1)
          .maybeSingle();

        if (workshopError) throw workshopError;

        if (!ws) {
          if (mounted) setWorkshopStatus("no-workshop");
          return;
        }

        if (mounted) setWorkshop(ws);

        // 3. participants
        const { data: participantsData } = await supabase
          .from("workshop_participants")
          .select("employee_id")
          .eq("workshop_id", ws.id);

        const isParticipant =
          participantsData?.some((participant) => participant.employee_id === emp) ?? false;

        if (!isParticipant) {
          if (mounted) {
            setParticipants(participantsData?.length || 0);
            setWorkshopStatus("not-participant");
          }
          return;
        }

        if (mounted) setParticipants(participantsData?.length || 0);

        // 4. modules
        const { data: moduleLinks } = await supabase
          .from("workshop_modules")
          .select(`
            module_id,
            modules (
              name_en,
              name_de,
              order_index,
              description_en,
              description_de
            )
          `)
          .eq("workshop_id", ws.id);

        const mods =
          moduleLinks
            ?.map((m: any) => m.modules)
            .filter(Boolean)
            .sort((a: any, b: any) => (a.order_index ?? 999) - (b.order_index ?? 999)) || [];
        if (mounted) setModules(mods);

        // 5. LOAD PRIORITIES
        const { data: prioData } = await supabase
          .from("workshop_participant_priorities")
          .select("priority_key")
          .eq("workshop_id", ws.id)
          .eq("employee_id", emp);

        if (mounted) setPriorities(prioData?.map((p) => p.priority_key) || []);

        // 6. LOAD LOGISTICS
        const { data: logData } = await supabase
          .from("workshop_participant_logistics")
          .select("*")
          .eq("workshop_id", ws.id)
          .eq("employee_id", emp)
          .maybeSingle();

        if (mounted && logData) {
          setLogistics({
            transport: logData.transport || "",
            drink: logData.drink || "",
            food: logData.food || "",
          });
        }

        if (mounted) setWorkshopStatus("ready");
      } catch (err) {
        console.error("init error", err);
        if (mounted) setWorkshopStatus("error");
      }
    }

    init();

    return () => {
      mounted = false;
    };
  }, []);

  async function togglePriority(key: string) {
    if (!employeeId || !workshop) return;

    const exists = priorities.includes(key);

    if (exists) {
      await supabase
        .from("workshop_participant_priorities")
        .delete()
        .eq("workshop_id", workshop.id)
        .eq("employee_id", employeeId)
        .eq("priority_key", key);

      setPriorities((p) => p.filter((k) => k !== key));
    } else {
      await supabase.from("workshop_participant_priorities").insert({
        workshop_id: workshop.id,
        employee_id: employeeId,
        priority_key: key,
      });

      setPriorities((p) => [...p, key]);
    }
  }

  async function saveLogistics(field: string, value: string) {
    if (!employeeId || !workshop) return;

    setLogistics((prev) => ({ ...prev, [field]: value }));

    await supabase.from("workshop_participant_logistics").upsert(
      {
        workshop_id: workshop.id,
        employee_id: employeeId,
        ...logistics,
        [field]: value,
      },
      { onConflict: "workshop_id,employee_id" }
    );
  }

  const handleBack = () => {
    if (role === "admin") {
      handlePageChange("ControlPanel");
    } else {
      handlePageChange("JourneyPillars");
    }
  };

  return (
    <Box
      sx={{
        position: "relative",
        minHeight: "100vh",

        // 🌌 BACKGROUND
        background: `
          radial-gradient(circle at 20% 20%, rgba(251, 2, 2, 0.08), transparent 40%),
          radial-gradient(circle at 80% 30%, rgba(255, 0, 0, 0.06), transparent 40%),
          #111010
        `,
      }}
    >
      {/* 🔙 BACK BUTTON */}
      <Box
        sx={{
          position: "absolute",
          top: isTablet ? 12 : 24,
          left: isTablet ? 12 : 24,
          zIndex: 10,
        }}
      >
        <IconButton
          onClick={handleBack}
          sx={{
            color: "white",
            background: "rgba(255,255,255,0.06)",
            backdropFilter: "blur(6px)",
            border: "1px solid rgba(255,255,255,0.1)",
            boxShadow: "0 0 15px rgba(255,255,255,0.1)",

            "&:hover": {
              background: "rgba(255,255,255,0.12)",
            },
          }}
        >
          <ArrowBackIcon />
        </IconButton>
      </Box>

      {/* CONTENT */}
      <Box
        sx={{
          px: isTablet ? 2 : 4,
          py: isTablet ? 4 : 8,
          display: "flex",
          justifyContent: "center",
        }}
      >
        {workshopStatus !== "ready" || !workshop ? (
          <Paper
            elevation={0}
            sx={{
              width: "100%",
              maxWidth: 720,
              borderRadius: 5,
              p: isTablet ? 3 : 5,
              mt: isTablet ? 4 : 6,
              background:
                "linear-gradient(135deg, rgba(255,255,255,0.05), rgba(255,255,255,0.02))",
              backdropFilter: "blur(14px)",
              border: "1px solid rgba(255,255,255,0.08)",
              boxShadow: "0 20px 60px rgba(0,0,0,0.4)",
              color: "white",
              textAlign: "center",
            }}
          >
            <Stack spacing={2.5} alignItems="center">
              <Box
                sx={{
                  width: 72,
                  height: 72,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "rgba(255,255,255,0.06)",
                  border: "1px solid rgba(255,255,255,0.1)",
                  boxShadow: "0 0 22px rgba(255,0,0,0.28)",
                }}
              >
                {workshopStatus === "loading" ? (
                  <CircularProgress size={32} sx={{ color: "#ff4d4d" }} />
                ) : workshopStatus === "not-participant" ? (
                  <PersonOffOutlinedIcon sx={{ color: "#ff4d4d", fontSize: 34 }} />
                ) : (
                  <EventBusyOutlinedIcon sx={{ color: "#ff4d4d", fontSize: 34 }} />
                )}
              </Box>

              <Typography variant={isTablet ? "h5" : "h4"} fontWeight={800}>
                {workshopStatus === "loading"
                  ? L("Checking your workshop", "Dein Workshop wird geprüft")
                  : workshopStatus === "not-participant"
                    ? L("No workshop assignment yet", "Noch keine Workshop-Zuordnung")
                    : L("No active workshop yet", "Noch kein aktiver Workshop")}
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  maxWidth: 560,
                  opacity: 0.85,
                  lineHeight: 1.7,
                }}
              >
                {workshopStatus === "loading"
                  ? L(
                      "Please wait a moment while we check your participant status.",
                      "Bitte warte einen Moment, während wir deinen Teilnehmerstatus prüfen."
                    )
                  : workshopStatus === "not-participant"
                    ? L(
                        "You are not added as a participant to any workshop yet. Please be patient while our team is working on it.",
                        "Du bist noch keinem Workshop als Teilnehmerin oder Teilnehmer hinzugefügt. Bitte hab etwas Geduld, während unser Team daran arbeitet."
                      )
                    : workshopStatus === "error"
                      ? L(
                          "We could not load your workshop right now. Please come back later.",
                          "Wir konnten deinen Workshop gerade nicht laden. Bitte komm später wieder."
                        )
                      : L(
                          "There is no currently active scheduled workshop yet. Please come back later.",
                          "Es gibt derzeit noch keinen aktiven geplanten Workshop. Bitte komm später wieder."
                        )}
              </Typography>

              {workshopStatus !== "loading" && (
                <Button
                  startIcon={<ArrowBackIcon />}
                  onClick={handleBack}
                  sx={{
                    mt: 1,
                    px: 3,
                    py: 1.2,
                    borderRadius: 3,
                    color: "white",
                    fontWeight: 700,
                    background: "linear-gradient(90deg, #710101, #ff0000)",
                    "&:hover": {
                      background: "linear-gradient(90deg, #480000, #940000)",
                    },
                  }}
                >
                  {L("Back to journey", "Zurück zur Journey")}
                </Button>
              )}
            </Stack>
          </Paper>
        ) : (
        <Paper
          elevation={0}
          sx={{
            width: "100%",
            maxWidth: 900,
            borderRadius: 5,
            p: isTablet ? 3 : 5,

            // ✨ CARD BACKGROUND
            background:
              "linear-gradient(135deg, rgba(255,255,255,0.05), rgba(255,255,255,0.02))",

            backdropFilter: "blur(14px)",

            border: "1px solid rgba(255,255,255,0.08)",
            boxShadow: "0 20px 60px rgba(0,0,0,0.4)",

            color: "white",
          }}
        >
          {/* TITLE */}
          <Typography
            variant={isTablet ? "h5" : "h4"}
            fontWeight={800}
            mb={3}
          >
            {workshop.title}
          </Typography>

          {/* META */}
          <Stack
            direction="row"
            spacing={3}
            flexWrap="wrap"
            mb={3}
            alignItems="center"
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <CalendarTodayOutlinedIcon sx={{ opacity: 0.7 }} />
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                {new Date(workshop.workshop_date).toLocaleDateString()}
              </Typography>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center">
              <GroupsOutlinedIcon sx={{ opacity: 0.7 }} />
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                {participants} {L("participants", "Teilnehmer")}
              </Typography>
            </Stack>
          </Stack>

          {/* OBJECTIVE */}
          <Stack direction="row" spacing={1.5} mb={2}>
            <InfoOutlinedIcon sx={{ color: "#ff4d4d", mt: "2px" }} />
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              {L(
                "This workshop helps you understand threats, prepare actions, and collaborate effectively in real-world scenarios.",
                "Dieser Workshop hilft dir, Bedrohungen zu verstehen, Maßnahmen vorzubereiten und effektiv in realen Situationen zusammenzuarbeiten."
              )}
            </Typography>
          </Stack>

          {/* TOPICS */}
          <Box>
            <Stack direction="row" spacing={1.5} mb={1.5}>
              <SchoolOutlinedIcon sx={{ color: "#00e676" }} />
              <Typography fontWeight={700}>
                {L("What you'll cover", "Was dich erwartet")}
              </Typography>
            </Stack>

            <Stack spacing={1} pl={4}>
              {modules.map((m, idx) => (
                <Box
                  key={idx}
                  sx={{
                    borderLeft: "2px solid rgba(255,255,255,0.14)",
                    pl: 1.5,
                    py: 0.25,
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 800, opacity: 0.92 }}>
                    {m.order_index ? `${m.order_index}. ` : ""}
                    {lang === "de" ? m.name_de || m.name_en : m.name_en}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.35, opacity: 0.72, lineHeight: 1.55 }}>
                    {lang === "de"
                      ? m.description_de || m.description_en
                      : m.description_en || m.description_de}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Box>
          
          {/* PERSONAL DETAILS */}
          <Box mt={5}>
            <Stack direction="row" spacing={1.5} mb={1.5}>
              <AutoAwesomeIcon sx={{ color: "#e6cf00" }} />
              <Typography fontWeight={700}>
                {L("Personal details for the workshop", "Persönliche Angaben für den Workshop")}
              </Typography>
            </Stack>

            {/* PRIORITIES */}
            <Typography mb={1}>
              {L(
                "What would matter most to you in a crisis?",
                "Was wäre dir in einer Krise am wichtigsten?"
              )}
            </Typography>

            <FormGroup>
              {priorityOptions.map((opt) => (
                <FormControlLabel
                  key={opt.key}
                  control={
                    <Checkbox
                      checked={priorities.includes(opt.key)}
                      onChange={() => togglePriority(opt.key)}
                      sx={{ color: "#fff" }}
                    />
                  }
                  label={lang === "de" ? opt.de : opt.en}
                />
              ))}
            </FormGroup>

            {/* LOGISTICS */}
            <Box mt={3}>
              <Typography mb={2}>
                {L("Optional logistics", "Optionale Logistik")}
              </Typography>

              <Stack spacing={2}>
                <TextField
                  fullWidth
                  value={logistics.transport}
                  onChange={(e) => saveLogistics("transport", e.target.value)}
                  placeholder={L(
                    "How will you arrive? Need parking?",
                    "Mit welchem Verkehrsmittel reist Du an? Brauchst Du einen Parkplatz?"
                  )}
                  sx={textfieldStyle}
                />

                <TextField
                  fullWidth
                  value={logistics.drink}
                  onChange={(e) => saveLogistics("drink", e.target.value)}
                  placeholder={L(
                    "What would you like to drink?",
                    "Welches Getränk dürfen wir dir anbieten?"
                  )}
                  sx={textfieldStyle}
                />

                <TextField
                  fullWidth
                  value={logistics.food}
                  onChange={(e) => saveLogistics("food", e.target.value)}
                  placeholder={L(
                    "What would you like to eat?",
                    "Was möchtest Du essen?"
                  )}
                  sx={textfieldStyle}
                />
              </Stack>
            </Box>
          </Box>

          <Box
            sx={{
              mt: 5,
              display: "flex",
              justifyContent: isTablet ? "stretch" : "flex-end",
            }}
          >
            <Button
              endIcon={<ArrowForwardIcon />}
              onClick={() => handlePageChange("BlackOutIntro")}
              fullWidth={isTablet}
              sx={{
                px: 3,
                py: 1.2,
                borderRadius: 3,
                color: "white",
                fontWeight: 800,
                background: "linear-gradient(90deg, #710101, #ff0000)",
                boxShadow: "0 0 24px rgba(255,0,0,0.35)",
                "&:hover": {
                  background: "linear-gradient(90deg, #480000, #940000)",
                  boxShadow: "0 0 30px rgba(255,0,0,0.5)",
                },
              }}
            >
              {L("Start the workshop!", "Workshop starten!")}
            </Button>
          </Box>
        </Paper>
        )}
      </Box>
    </Box>
  );
};

const textfieldStyle = {
  '& .MuiInputBase-root': { color: '#fff' },
  '& .MuiOutlinedInput-root': {
    '& fieldset': { borderColor: 'rgba(255,255,255,0.7)' },
    '&:hover fieldset': { borderColor: '#fff' },
    '&.Mui-focused fieldset': { borderColor: '#fff' },
  },
  '& .MuiInputBase-input::placeholder': {
    color: 'rgba(255,255,255,0.5)',
    opacity: 1,
  },
};
